import { Controller } from '@hotwired/stimulus'

export default class SelectController extends Controller {
  static targets = ['native', 'trigger', 'label', 'dropdown', 'list']
  static classes = ['open', 'selected']

  declare readonly nativeTarget: HTMLSelectElement
  declare readonly triggerTarget: HTMLButtonElement
  declare readonly labelTarget: HTMLElement
  declare readonly dropdownTarget: HTMLElement
  declare readonly listTarget: HTMLElement
  declare readonly openClasses: string[]
  declare readonly selectedClasses: string[]

  connect() {
    this.buildCustomOptions()
    this.updateTriggerLabel()

    document.addEventListener('click', this.onClickOutside)

    this.element.addEventListener('keydown', this.onKeydown)
  }

  disconnect() {
    document.removeEventListener('click', this.onClickOutside)

    this.element.removeEventListener('keydown', this.onKeydown)
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.triggerTarget.disabled) return

    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.dropdownTarget.classList.remove('hidden')
    this.element.classList.add(...this.openClasses)

    // Focus the selected or first option
    const selectedOption = this.listTarget.querySelector(
      '[aria-selected="true"]',
    ) as HTMLElement
    const firstOption = this.listTarget.firstElementChild as HTMLElement

    // Small timeout to allow the browser to paint the visible list so focus works
    requestAnimationFrame(() => {
      if (selectedOption) {
        selectedOption.focus()
      } else if (firstOption) {
        firstOption.focus()
      }
    })
  }

  close(restoreFocus = true) {
    if (!this.isOpen) return

    this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.dropdownTarget.classList.add('hidden')
    this.element.classList.remove(...this.openClasses)

    if (restoreFocus) {
      this.triggerTarget.focus()
    }
  }

  select(event: Event): void {
    const target = event.currentTarget as HTMLElement
    this.selectOption(target)
  }

  /**
   * Refreshes the custom dropdown UI (trigger label + list item states)
   * from the current native <select> value.
   *
   * Call this after programmatically setting nativeTarget.value from
   * outside the controller (e.g. applying URL selection params).
   *
   * @param value - Optional explicit value to sync to. If omitted, reads
   *                the current nativeTarget.value.
   */
  syncFromNative(value?: string): void {
    const resolved = value ?? this.nativeTarget.value
    if (value !== undefined) {
      this.nativeTarget.value = resolved
    }
    this.updateSelectionState(resolved)
    this.updateTriggerLabelForValue(resolved)
  }

  // Handle keyboard navigation
  onKeydown = (event: Event) => {
    if (this.triggerTarget.disabled) return

    const keyboardEvent = event as KeyboardEvent
    const { key } = keyboardEvent
    const options = Array.from(this.listTarget.children) as HTMLElement[]

    // If closed and user presses 'Enter', 'Space', 'ArrowDown', 'ArrowUp' -> Open it
    if (!this.isOpen && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(key)) {
      event.preventDefault()
      this.open()
      return
    }

    // If open...
    if (this.isOpen) {
      if (key === 'Escape') {
        keyboardEvent.preventDefault()
        this.close()
        return
      }

      const activeElement = document.activeElement as HTMLElement
      const currentIndex = options.indexOf(activeElement)

      if (key === 'ArrowDown') {
        keyboardEvent.preventDefault()
        const nextIndex = (currentIndex + 1) % options.length
        options[nextIndex]?.focus()
      } else if (key === 'ArrowUp') {
        keyboardEvent.preventDefault()
        const prevIndex = (currentIndex - 1 + options.length) % options.length
        options[prevIndex]?.focus()
      } else if (key === 'Enter' || key === ' ') {
        keyboardEvent.preventDefault()
        if (currentIndex >= 0) {
          this.selectOption(options[currentIndex])
        }
      } else if (key === 'Tab') {
        // Allow Tab to move focus away and close
        this.close(false)
      }
    }
  }

  private selectOption(target: HTMLElement) {
    const value = target.dataset.value

    if (target.dataset.disabled === 'true') return

    this.nativeTarget.value = value || ''
    this.nativeTarget.dispatchEvent(new Event('change', { bubbles: true }))

    this.updateSelectionState(value || '')
    this.close()
    this.updateTriggerLabel()
  }

  private updateSelectionState(value: string) {
    const options = Array.from(this.listTarget.children) as HTMLElement[]
    options.forEach((option) => {
      const isSelected = option.dataset.value === value
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false')
      if (isSelected) {
        option.classList.add('bg-gray-100') // Visual highlight
      } else {
        option.classList.remove('bg-gray-100')
      }
    })
  }

  private get isOpen() {
    return this.triggerTarget.getAttribute('aria-expanded') === 'true'
  }

  private updateTriggerLabel(): void {
    const selectedOption = this.nativeTarget.selectedOptions[0]
    if (selectedOption) {
      this.labelTarget.textContent = selectedOption.text
    }
  }

  /**
   * Updates the trigger label using a specific value rather than relying
   * on nativeTarget.selectedOptions (which may lag behind after a
   * programmatic value change).
   */
  private updateTriggerLabelForValue(value: string): void {
    const option = Array.from(this.nativeTarget.options).find(
      (o) => o.value === value,
    )
    if (option) {
      this.labelTarget.textContent = option.text
    }
  }

  private buildCustomOptions() {
    this.listTarget.innerHTML = ''
    Array.from(this.nativeTarget.options).forEach((option) => {
      if (option.disabled && option.value === '') return

      const li = document.createElement('li')
      li.role = 'option'
      // Use tabindex="-1" so it can be focused programmatically but not via Tab
      li.tabIndex = -1
      li.className =
        'flex items-center justify-between px-[10px] py-2 cursor-pointer hover:bg-gray-50 transition-colors focus:bg-gray-100 focus:outline-none'
      li.dataset.value = option.value
      li.dataset.action = 'click->select#select'

      const isSelected = this.nativeTarget.value === option.value
      li.setAttribute('aria-selected', isSelected ? 'true' : 'false')
      if (isSelected) li.classList.add('bg-gray-100')

      const textSpan = document.createElement('span')
      textSpan.className = 'font-bold text-body'
      textSpan.textContent = option.text
      li.appendChild(textSpan)

      if (option.disabled) {
        li.dataset.disabled = 'true'
        li.classList.add('opacity-50', 'cursor-not-allowed')
        li.setAttribute('aria-disabled', 'true')
      } else if (option.dataset.soldOut === 'true') {
        li.dataset.soldOut = 'true'
        li.classList.add('opacity-50')

        const waitlistSpan = document.createElement('span')
        waitlistSpan.className = 'text-gray-400 text-xs'
        waitlistSpan.textContent = 'Waitlist'
        li.appendChild(waitlistSpan)
      }

      this.listTarget.appendChild(li)
    })
  }

  onClickOutside = (event: Event) => {
    if (!this.element.contains(event.target as Node)) {
      this.close(false)
    }
  }
}
